import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, IndianRupee, AlertCircle, Gauge } from 'lucide-react';
import { KpiChip } from '../../components/ui';
import { formatInr } from './homeDashboardUtils';

const LOADING_DELTA = { value: 'Loading…', direction: 'flat' };

export default function DashboardStats({
  occupancyPercentage,
  bookedSlotsToday,
  totalSlotsToday,
  bookingsThisWeek,
  bookingsLastWeek,
  revenueThisMonth,
  revenueLastMonth,
  revenueDelta,
  pendingRequestCount,
  summaryLoading,
  paymentsSummary,
  overdue,
  totalOpsTasks,
  completedOpsTasks,
  openTicketsCount,
}) {
  const booked = Math.round(bookedSlotsToday || 0);
  const total = Math.round(totalSlotsToday || 0);
  const weekDelta = (bookingsThisWeek || 0) - (bookingsLastWeek || 0);
  const slotLabel = total === 1 ? 'slot' : 'slots';

  const opsRemaining = Math.max(0, (totalOpsTasks ?? 0) - (completedOpsTasks ?? 0));
  const pendingPayments = paymentsSummary?.pendingCount ?? 0;
  const overdueCount = overdue?.length ?? 0;
  const summaryItems = [
    { key: 'payments', count: pendingPayments, label: 'payments pending', to: '/dashboard/payments' },
    { key: 'equipment', count: overdueCount, label: 'equipment overdue', to: '/dashboard/operations' },
    { key: 'ops', count: opsRemaining, label: 'ops tasks left', to: '/dashboard/operations/daily' },
    { key: 'tickets', count: openTicketsCount || 0, label: 'tickets open', to: '/dashboard/help-center' },
  ];
  const breakdown = [
    { key: 'requests', count: pendingRequestCount || 0, label: 'booking requests', to: '/dashboard/booking-requests' },
    ...(summaryLoading ? [] : summaryItems),
  ].filter((b) => b.count > 0);
  const summaryTotal = summaryLoading ? 0 : summaryItems.reduce((sum, b) => sum + b.count, 0);
  const totalPending = (pendingRequestCount || 0) + summaryTotal;

  const revenueDeltaLabel = (() => {
    if (revenueLastMonth === 0 && revenueThisMonth === 0) return 'No revenue last 2 months';
    if (revenueLastMonth === 0 && revenueThisMonth > 0) return 'New this month';
    if (revenueDelta === 0) return 'Same as last month';
    const sign = revenueDelta > 0 ? '+' : '−';
    return `${sign}${formatInr(Math.abs(Math.round(revenueDelta)))} vs last month`;
  })();

  const pendingFooter = summaryLoading && totalPending === 0 ? (
    <p className="mt-1 text-xs text-ga-text-subtle">Loading…</p>
  ) : totalPending === 0 ? (
    <p className="mt-1 text-xs text-ga-text-subtle">You&apos;re all caught up</p>
  ) : (
    <div className="mt-1 space-y-0.5 text-xs text-ga-text-muted">
      {breakdown.map((b) => (
        <Link key={b.key} to={b.to} className="block hover:text-ga-primary hover:underline">
          {b.count} {b.label}
        </Link>
      ))}
      {summaryLoading && (
        <p className="text-ga-text-subtle">Loading other actions…</p>
      )}
    </div>
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
      <KpiChip
        label="Today's occupancy"
        value={summaryLoading ? '–' : `${occupancyPercentage}%`}
        icon={Gauge}
        accent="primary"
        delta={
          summaryLoading
            ? LOADING_DELTA
            : { value: `${booked} of ${total} ${slotLabel} booked`, direction: 'flat' }
        }
      />

      <KpiChip
        label="Bookings this week"
        value={summaryLoading ? '–' : bookingsThisWeek || 0}
        icon={CalendarDays}
        accent="info"
        delta={
          summaryLoading
            ? LOADING_DELTA
            : {
                value: `${weekDelta >= 0 ? '+' : ''}${weekDelta} vs last week`,
                direction: weekDelta > 0 ? 'up' : weekDelta < 0 ? 'down' : 'flat',
              }
        }
      />

      <KpiChip
        label="Revenue this month"
        value={summaryLoading ? '–' : formatInr(revenueThisMonth)}
        icon={IndianRupee}
        accent="success"
        delta={
          summaryLoading
            ? LOADING_DELTA
            : {
                value: revenueDeltaLabel,
                direction: revenueDelta > 0 ? 'up' : revenueDelta < 0 ? 'down' : 'flat',
              }
        }
      />

      <KpiChip
        label="Pending actions"
        value={summaryLoading && totalPending === 0 ? '–' : totalPending}
        icon={AlertCircle}
        accent={totalPending > 0 ? 'warning' : 'primary'}
        align="start"
        footer={pendingFooter}
      />
    </div>
  );
}
